import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import QuestionModal from '../../../../modals/QuestionModal';
import ViewImageSectionModal from '../../../../modals/ViewImageSectionModal';
import UploadDocumentsModal from '../../../../modals/UploadDocumentsModal';
import {
  setQuestionModalVisible,
  setViewImageSectionModalVisible,
  setViewUploadDocumentsModalVisible,
  markSectionAsHandled,
} from '../../../CreateVideoNewPage/store/slices/editorUISlice';
import { play, pause, seek } from '../../../CreateVideoNewPage/store/slices/playbackSlice';
import { selectItem, selectTotalDuration } from '../../../CreateVideoNewPage/store/slices/timelineSlice';
import { updateAsset } from '../../../CreateVideoNewPage/store/slices/assetsSlice';
import { useMeetingViewStore } from '../../../../store';
import UserService from '../../../../services/UserService';

export default function ViewerModalsContainer({
  currentItem,
  sections,
  refetchMeeting,
  onModalClose,
}) {
  const dispatch = useDispatch();
  const timelineItems = useSelector((state) => state.timeline.items);
  const totalDuration = useSelector(selectTotalDuration);
  const setCurrentQuestion = useMeetingViewStore((state) => state.setCurrentQuestion);
  const assets = useSelector((state) => [
    ...state.assets.videos,
    ...state.assets.images,
    ...state.assets.documents,
    ...state.assets.questions,
    ...state.assets.texts,
    ...state.assets.audios,
    ...state.assets.assignedSections,
  ]);
  const { data: user } = useQuery('me', UserService.getMe, {
    enabled: false, // Only use cached data
  });

  // Redux selectors for modal visibility
  const isQuestionModalVisible = useSelector((state) => state.editorUI.isQuestionModalVisible);
  const isViewImageSectionModalVisible = useSelector((state) => state.editorUI.isViewImageSectionModalVisible);
  const isViewUploadDocumentsModalVisible = useSelector((state) => state.editorUI.isViewUploadDocumentsModalVisible);

  // Find current section from currentItem
  const currentSection = currentItem && currentItem.metadata?.sectionId
    ? sections.find((s) => s.id === currentItem.metadata.sectionId)
    : null;

  // Find next item for continue functionality
  const getNextItem = () => {
    if (!currentItem) return null;
    const currentIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
    if (currentIndex >= 0 && currentIndex < timelineItems.length - 1) {
      return timelineItems[currentIndex + 1];
    }
    return null;
  };

  // Handle modal close - optionally continue to next section
  const handleModalClose = (options) => {
    const shouldContinue =
      options && typeof options === 'object' && 'shouldContinue' in options
        ? options.shouldContinue !== false
        : true;

    // If user explicitly closed (e.g., clicked X), pause and do not advance
    if (!shouldContinue) {
      dispatch(pause());
      if (onModalClose) {
        onModalClose();
      }
      return;
    }

    const nextItem = getNextItem();
    if (nextItem) {
      dispatch(selectItem(nextItem.id));
      dispatch(seek(nextItem.start_time));
      dispatch(play());
    } else {
      // Last item - just pause at end
      dispatch(seek(totalDuration));
    }
    if (onModalClose) {
      onModalClose();
    }
  };

  // Track if the last answer submission was an update (to prevent auto-continue)
  const isLastAnswerUpdateRef = useRef(false);

  // Mark section as handled when answer is submitted (before modal closes)
  // Also update the asset with the new answer
  const handleQuestionAnswerSubmitted = (answerText, isUpdate = false) => {
    // Store whether this is an update
    isLastAnswerUpdateRef.current = isUpdate;
    
    const sectionId = currentSection?.id;
    if (sectionId) {
      dispatch(markSectionAsHandled(sectionId));
      
      // Update the asset with the new answer
      if (currentItem && user) {
        const asset = assets.find((a) => a.id === currentItem.asset_id);
        if (asset && asset.type === 'question') {
          // Create a new answer object (matching backend structure)
          const newAnswer = {
            id: `temp-${Date.now()}`, // Temporary ID, will be replaced on refetch
            text: answerText,
            User: {
              id: user.id,
              name: user.name,
              photoPath: user.photoPath,
            },
            createdAt: new Date().toISOString(),
          };
          
          // Update asset metadata with the new answer
          // If user already has an answer, replace it; otherwise add it
          const currentAnswers = asset.metadata?.questionAnswers || [];
          const existingAnswerIndex = currentAnswers.findIndex(
            (answr) => answr.User?.id === user.id
          );
          
          let updatedAnswers;
          if (existingAnswerIndex >= 0) {
            // Replace existing answer
            updatedAnswers = [...currentAnswers];
            updatedAnswers[existingAnswerIndex] = newAnswer;
          } else {
            // Add new answer
            updatedAnswers = [...currentAnswers, newAnswer];
          }
          
          dispatch(updateAsset({
            id: asset.id,
            changes: {
              metadata: {
                ...asset.metadata,
                questionAnswers: updatedAnswers,
              },
            },
          }));
        }
      }
    }
  };

  // Handle question modal close
  const handleQuestionModalClose = (options) => {
    const shouldContinue =
      options && typeof options === 'object' && 'shouldContinue' in options
        ? options.shouldContinue !== false
        : true;
    const shouldMarkHandled =
      options && typeof options === 'object' && 'shouldMarkHandled' in options
        ? options.shouldMarkHandled !== false
        : shouldContinue;

    const sectionId = currentSection?.id;
    dispatch(setQuestionModalVisible(false));
    if (sectionId && shouldMarkHandled) {
      // Section should already be marked as handled when answer was submitted
      // But mark it here too in case user closes without answering
      dispatch(markSectionAsHandled(sectionId));
    }
    
    // If this was an update (user-initiated edit), don't auto-continue
    // Just close the modal and pause, don't seek to next section
    if (isLastAnswerUpdateRef.current) {
      dispatch(pause());
      isLastAnswerUpdateRef.current = false; // Reset flag
      return;
    }
    
    // For new answers, continue to next section as normal unless explicitly told not to
    handleModalClose({ ...options, shouldContinue });
  };

  // Handle image modal close
  const handleImageModalClose = (options) => {
    const shouldContinue =
      options && typeof options === 'object' && 'shouldContinue' in options
        ? options.shouldContinue !== false
        : true;
    const shouldMarkHandled =
      options && typeof options === 'object' && 'shouldMarkHandled' in options
        ? options.shouldMarkHandled !== false
        : shouldContinue;

    const sectionId = currentSection?.id;
    dispatch(setViewImageSectionModalVisible(false));
    if (sectionId && shouldMarkHandled) {
      dispatch(markSectionAsHandled(sectionId));
    }
    handleModalClose({ ...options, shouldContinue });
  };

  // Handle document modal close
  const handleDocumentModalClose = (options) => {
    const shouldContinue =
      options && typeof options === 'object' && 'shouldContinue' in options
        ? options.shouldContinue !== false
        : true;
    const shouldMarkHandled =
      options && typeof options === 'object' && 'shouldMarkHandled' in options
        ? options.shouldMarkHandled !== false
        : shouldContinue;

    const sectionId = currentSection?.id;
    dispatch(setViewUploadDocumentsModalVisible(false));
    if (sectionId && shouldMarkHandled) {
      dispatch(markSectionAsHandled(sectionId));
    }
    handleModalClose({ ...options, shouldContinue });
  };

  // Set current question in store when question section is current (for QuestionModal)
  useEffect(() => {
    if (currentSection?.type === 'question' && currentSection.Question) {
      setCurrentQuestion(currentSection.Question);
    }
  }, [currentSection, setCurrentQuestion]);

  // Get user's existing answer if available
  const getExistingAnswer = () => {
    if (currentSection?.type !== 'question' || !currentSection.Question || !user) {
      return null;
    }
    const userAnswer = currentSection.Question.Answers?.find(
      (answr) => answr.User?.id === user.id
    );
    return userAnswer || null;
  };
  const existingAnswer = getExistingAnswer();

  // Get image attachments for ViewImageSectionModal
  // Image sections can have SectionAttachments OR a direct image URL
  const getImageAttachments = () => {
    if (currentSection?.type !== 'image') {
      return [];
    }
    if (currentSection.SectionAttachments && currentSection.SectionAttachments.length > 0) {
      return currentSection.SectionAttachments.filter((att) => att.type === 'image');
    }
    if (currentSection.signedS3Url) {
      return [{ signedS3Url: currentSection.signedS3Url, type: 'image', name: currentSection.title || 'Image' }];
    }
    return [];
  };
  const imageAttachments = getImageAttachments();

  // Get document attachments for UploadDocumentsModal
  const documentAttachments = currentSection?.type === 'document' && currentSection.SectionAttachments
    ? currentSection.SectionAttachments.filter((att) => att.type === 'document')
    : [];

  return (
    <>
      {/* Question Modal */}
      {currentSection?.type === 'question' && (
        <QuestionModal
          show={isQuestionModalVisible}
          handleClose={handleQuestionModalClose}
          onAnswerSubmitted={handleQuestionAnswerSubmitted}
          refetchMeeting={refetchMeeting}
          preview={false}
          shouldDisableQuestionModalAnimationOnEnter={false}
          shouldDisableQuestionModalAnimationOnExit={false}
          existingAnswer={existingAnswer}
        />
      )}

      {/* Image Modal */}
      {currentSection?.type === 'image' && imageAttachments.length > 0 && (
        <ViewImageSectionModal
          show={isViewImageSectionModalVisible}
          handleClose={handleImageModalClose}
          images={imageAttachments}
          text={currentSection.text}
        />
      )}

      {/* Document Modal */}
      {currentSection?.type === 'document' && documentAttachments.length > 0 && (
        <UploadDocumentsModal
          show={isViewUploadDocumentsModalVisible}
          handleClose={handleDocumentModalClose}
          initialFiles={documentAttachments}
          readOnly
        />
      )}
    </>
  );
}

